import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { SARStatusPill } from '../StatusPills';
import { sarApi } from '../../lib/api';
import FollowUpDialog from './FollowUpDialog';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null);

/**
 * Request tab: every request to this organisation — the initial one and any
 * follow-ups — each with its own status, plus "ask for what is missing",
 * a guided modal (FollowUpDialog) rather than a free-text reply — tick the
 * gaps we noticed and/or a common reason, write anything else, done.
 *
 * Records the person uploaded themselves count as a request already
 * answered: with no request here yet but documents on file, the empty
 * state leads with asking for what's missing from those (a follow-up that
 * refers to their own earlier request) rather than a fresh request for
 * everything.
 */
export const OrgRequests = ({ org, sars = [], gaps = [], hasOwnRecords = false, onChanged, showFollowUp = true, className = '' }) => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const initial = sars.find((s) => s.kind === 'initial');
  const followUps = sars.filter((s) => s.kind !== 'initial');

  const createInitial = async () => {
    setCreating(true);
    try {
      const res = await sarApi.generate({ organisation_id: org.id, organisation_name: org.name });
      toast.success('Request drafted.');
      navigate(`/sar/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The request could not be created.');
    } finally {
      setCreating(false);
    }
  };

  const Row = ({ sar }) => (
    <button type="button" onClick={() => navigate(`/sar/${sar.id}`)} className="w-full text-left rounded-lg bg-ground hover:bg-inset transition-colors duration-state p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`org-sar-${sar.id}`}>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading text-[17px] font-semibold tracking-[-0.02em] text-ink">
            {sar.kind === 'initial' ? 'Your request for everything' : sar.follows_own_request ? 'Follow-up to your own request' : 'Follow-up'}
          </span>
          <SARStatusPill status={sar.status} daysRemaining={sar.days_remaining} size="sm" />
        </div>
        <p className="pd-mono">
          {sar.sent_at ? `SENT ${fmt(sar.sent_at)}` : `DRAFTED ${fmt(sar.created_at)}`}
          {sar.deadline ? ` · DEADLINE ${fmt(sar.deadline)}` : ''}
          {sar.messages ? ` · ${sar.messages} MESSAGE${sar.messages === 1 ? '' : 'S'}` : ''}
          {sar.documents_received ? ` · ${sar.documents_received} DOCUMENT${sar.documents_received === 1 ? '' : 'S'} RECEIVED` : ''}
        </p>
        {sar.scope?.length > 0 && (
          <ul className="text-sm text-ink-70 list-disc pl-5">{sar.scope.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}{sar.scope.length > 3 && <li>and {sar.scope.length - 3} more</li>}</ul>
        )}
      </div>
      <span className="text-sm font-semibold text-brand shrink-0">Open →</span>
    </button>
  );

  return (
    <div className={`flex flex-col gap-6 ${className}`} data-testid="org-requests">
      {org.unresolved ? (
        <div className="rounded-lg bg-amber-wash p-5">
          <p className="text-[15px] font-semibold text-amber-ink">Nothing can be sent yet</p>
          <p className="text-sm text-ink-70 mt-1">This is a placeholder for a council you have not named. Name it on the Your time here tab, then send a request.</p>
        </div>
      ) : sars.length === 0 && hasOwnRecords ? (
        <div className="flex-1 flex flex-col justify-center rounded-lg bg-ground py-10 px-6 text-center" data-testid="org-own-records">
          <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mx-auto mb-4" />
          <p className="font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink mb-1">You already have records from them</p>
          <p className="text-base text-ink-55 max-w-md mx-auto mb-5">
            You added these yourself. If something is missing or incomplete, send {org.name} a follow-up that refers to
            your earlier request. They must answer it within a month.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button onClick={() => setFollowUpOpen(true)} data-testid="org-own-records-followup">
              {gaps.length ? `Ask for what is missing (${gaps.length} gap${gaps.length === 1 ? '' : 's'} noticed)` : 'Ask for what is missing'}
            </Button>
            <Button variant="ghost" onClick={createInitial} disabled={creating} data-testid="org-create-request">
              {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}Request everything again
            </Button>
          </div>
        </div>
      ) : sars.length === 0 ? (
        <div className="flex-1 flex flex-col justify-center rounded-lg bg-ground py-10 px-6 text-center" data-testid="org-no-request">
          <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mx-auto mb-4" />
          <p className="font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink mb-1">No request yet</p>
          <p className="text-base text-ink-55 max-w-md mx-auto mb-5">A subject access request is your legal right to everything {org.name} holds about you. Once sent, they have one month to reply.</p>
          <Button onClick={createInitial} disabled={creating} data-testid="org-create-request">
            {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}Draft a request
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {initial && <Row sar={initial} />}
          {followUps.length > 0 && <p className="pd-eyebrow pt-2">Follow-ups</p>}
          {followUps.map((s) => <Row key={s.id} sar={s} />)}
        </div>
      )}

      {showFollowUp && !org.unresolved && !(sars.length === 0 && hasOwnRecords) && (
        <div className="rounded-lg bg-ground p-5 space-y-4" data-testid="org-followup-card">
          <div>
            <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">Ask for what is missing</h3>
            <p className="text-sm text-ink-55 mt-1">A short, targeted request. They must answer it separately, within a month, and say in writing if a record never existed or was destroyed.</p>
          </div>
          <Button variant="secondary" onClick={() => setFollowUpOpen(true)} data-testid="org-followup-start">
            {gaps.length ? `Draft a follow-up (${gaps.length} gap${gaps.length === 1 ? '' : 's'} noticed)` : 'Draft a follow-up'}
          </Button>
        </div>
      )}

      <FollowUpDialog
        open={followUpOpen}
        onOpenChange={setFollowUpOpen}
        org={org}
        gaps={gaps}
        initialSarId={initial?.id}
        hasOwnRecords={hasOwnRecords}
        onCreated={onChanged}
      />
    </div>
  );
};

/**
 * "Ask for what is missing" on its own, as the green box under the requests
 * on Requests & data once something has arrived to ask about. Opens the same
 * guided FollowUpDialog.
 */
export const FollowUpBox = ({ org, sars = [], gaps = [], hasOwnRecords = false, onChanged, className = '' }) => {
  const [open, setOpen] = useState(false);
  const initial = sars.find((s) => s.kind === 'initial');
  return (
    <div className={`rounded-lg bg-brand-wash p-5 flex flex-col gap-3 ${className}`} data-testid="org-followup-card">
      <div>
        <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-brand-ink">Ask for what is missing</h3>
        <p className="text-[15px] text-ink-70 mt-1 max-w-[60ch]">A short, targeted request. They must answer it separately, within a month, and say in writing if a record never existed or was destroyed.</p>
      </div>
      <div className="mt-auto">
        <Button onClick={() => setOpen(true)} data-testid="org-followup-start">
          {gaps.length ? `Draft a follow-up (${gaps.length} gap${gaps.length === 1 ? '' : 's'} noticed)` : 'Draft a follow-up'}
        </Button>
      </div>
      <FollowUpDialog open={open} onOpenChange={setOpen} org={org} gaps={gaps} initialSarId={initial?.id} hasOwnRecords={hasOwnRecords} onCreated={onChanged} />
    </div>
  );
};

export default OrgRequests;
